import { EPRangePicker, SpinCentered } from "@/comp";
import { message, Modal } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { CreateNewReportModalStyles } from "./styled";
import { EPButton } from "@/components";
import { useStores } from "@/hooks/use-stores";
import { useState, useCallback } from "react";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { observer } from "mobx-react";
import { transformDateRangeToReportPayload } from "./ReportPage.utils";

const MAX_RANGE_DAYS = 14;

const CreateNewReportModal = observer(({ open, onClose }: { open: boolean, onClose: () => void }) => {
  const { t } = useTranslation();
  const { reportStore } = useStores();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(null);

  const handleOnDateRangeChange = (dates: [Dayjs, Dayjs] | null) => {
    setDateRange(dates);
    // Track the first selected date to limit the range
    if (dates?.[0] && !dates?.[1]) {
      setSelectedStartDate(dates[0]);
    } else if (!dates?.[0] && !dates?.[1]) {
      setSelectedStartDate(null);
    }
  }

  const disabledDate = useCallback((current: Dayjs) => {
    // Disable future dates
    const isFutureDate = current && current.isAfter(dayjs().endOf('day'));
    if (isFutureDate) return true;

    // If a start date is selected, limit to 14 days range
    if (selectedStartDate) {
      const isBeforeStart = current.isBefore(selectedStartDate, 'day');
      const isMoreThan14Days = current.diff(selectedStartDate, 'days') > MAX_RANGE_DAYS;
      return isBeforeStart || isMoreThan14Days;
    }

    return false;
  }, [selectedStartDate]);
  
  const handleOnGenerateReport = async() => {
    try {
        if (dateRange) {
            await reportStore.generateReport(
              transformDateRangeToReportPayload(dateRange, reportStore)
            );
            message.success(t('reportRequestedSuccessfully'));
            onClose();
          }else{
              message.error(t('pleaseSelectADateRange'));
              return;
          }
    } catch (error) {
        if (error.response?.status === 400) {
            message.error(error?.response?.data?.message);
        } else {
            message.error(t('failedToRequestReport'));
        }
        
      } finally {
        // Refresh reports list maintaining current pagination
        await reportStore.getReports(reportStore.pagination.page, reportStore.pagination.pageSize);
      }
    
  }
  if (reportStore.isLoadingGenerateDeletePublishReport) {   
    return <SpinCentered />;
  }
  
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={480}
      title={t('createNewReport')}
    >
      <CreateNewReportModalStyles>
        <div className="create-new-report-modal-content">
            <div className="time-frame-info">
              <InfoCircleOutlined className="info-icon" />
              <span>{t('reportTimeFrameUpTo2Weeks')}</span>
            </div>
            <div className="date-range-picker-container">
            <div className="select-dates"> {t('selectDates')}</div>  
                <EPRangePicker 
                  value={dateRange} 
                  onCalendarChange={handleOnDateRangeChange} 
                  startDateDisabledDate={disabledDate} 
                  disableFutureDates={true}
                  {...{ defaultPickerValue: [dayjs(), selectedStartDate?.date(MAX_RANGE_DAYS + 1) || dayjs()] }}
                />
            </div>
            <div className="report-description-container">
              <InfoCircleOutlined className="note-icon" />
              <span className="report-description-text"> {t('noteForReportGeneration')}</span>
            </div>
            <div className="straight-line"></div>
            <div className="create-report-button-container">
                <EPButton type="primary" onClick={handleOnGenerateReport}>
                    {t('generateReport')}
                </EPButton>
            </div>
        </div>
      </CreateNewReportModalStyles>
    </Modal>
  );
});

export default CreateNewReportModal;